import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

// Shared presentational pieces for the Customer Actions tabs.

export function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-border-subtle p-5 ${className}`}>{children}</div>;
}

export function CardHeader({ title, badge, hint }) {
  return (
    <>
      <div className='flex items-center justify-between border-b border-border-subtle pb-3 mb-3 gap-2'>
        <h3 className='text-body-sm font-bold text-text-primary'>{title}</h3>
        {badge && <span className='text-[11px] font-semibold text-text-tertiary bg-gray-100 px-2 py-0.5 rounded-md whitespace-nowrap'>{badge}</span>}
      </div>
      {hint && <p className='text-caption text-text-tertiary leading-relaxed mb-4'>{hint}</p>}
    </>
  );
}

export function Reading({ children }) {
  return (
    <div className='mt-6 pt-3 border-t border-border-subtle text-caption text-text-secondary italic'>
      <strong className='text-text-primary font-semibold'>Reading:</strong> {children}
    </div>
  );
}

export function StatCard({ label, value, note, color = "text-text-primary", labelColor = "text-text-tertiary" }) {
  return (
    <div className='bg-white rounded-2xl border border-border-subtle p-5 shadow-xs'>
      <span className={`text-caption font-bold uppercase tracking-wider ${labelColor}`}>{label}</span>
      <p className={`text-metric mt-2 ${color}`}>{value}</p>
      {note && <p className='text-[10px] text-text-tertiary mt-2 font-mono leading-snug break-words'>{note}</p>}
    </div>
  );
}

export function EmptyState({ title, children }) {
  return (
    <div className='bg-white rounded-2xl border border-dashed border-border-default p-10 text-center'>
      <p className='text-body-sm font-semibold text-text-secondary'>{title}</p>
      {children && <p className='text-caption text-text-tertiary mt-1'>{children}</p>}
    </div>
  );
}

// Donut with a centred total and a legend of {name, value, percentage, color} rows.
export function Donut({ data, total, unit, mono = false }) {
  return (
    <div className='flex flex-col sm:flex-row items-center justify-around gap-6 flex-1'>
      <div className='relative shrink-0' style={{ width: 160, height: 160 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie data={data} cx='50%' cy='50%' innerRadius={48} outerRadius={64} paddingAngle={2} dataKey='value'>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
          <span className='text-h3 font-bold text-text-primary leading-none'>{total}</span>
          <span className='text-[9px] text-text-tertiary font-semibold uppercase tracking-wider mt-1'>{unit}</span>
        </div>
      </div>
      <div className='flex-1 w-full space-y-2'>
        {data.map((item) => (
          <div key={item.name} className='flex items-center justify-between text-body-xs font-semibold'>
            <div className='flex items-center gap-2'>
              <span className='w-2.5 h-2.5 rounded-xs' style={{ backgroundColor: item.color }} />
              {mono ? <code className='text-text-secondary bg-gray-50 px-1 py-0.5 rounded border'>{item.name}</code> : <span className='text-text-secondary'>{item.name}</span>}
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-text-primary font-bold'>{item.value}</span>
              <span className='text-text-tertiary text-[10px] font-normal'>({item.percentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EventBadge({ type }) {
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${eventBadgeClass(type)}`}>{type}</span>;
}

export function eventBadgeClass(type) {
  switch (type) {
    case "finding.marked_done":
      return "bg-accent-green/10 text-accent-green border-accent-green/20";
    case "finding.discarded":
      return "bg-severity-high/10 text-severity-high border-severity-high/20";
    case "finding.assigned":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "finding.drag_reordered":
      return "bg-accent-purple/10 text-accent-purple border-accent-purple/20";
    case "finding.shared":
      return "bg-amber-500/10 text-amber-700 border-amber-500/20";
    case "finding.external_url_clicked":
      return "bg-teal-500/10 text-teal-700 border-teal-500/20";
    case "ranking.viewed":
    case "activity.viewed":
    case "journey.navigation":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "release_announcement.viewed":
    case "release_announcement.dismissed":
    case "release_announcement.blog_clicked":
    case "release_announcement.video_played":
      return "bg-accent-purple/10 text-accent-purple border-accent-purple/20";
    case "notification.run_announcement_sent":
      return "bg-teal-500/10 text-teal-700 border-teal-500/20";
    default:
      return "bg-indigo-500/10 text-indigo-600 border-indigo-500/20";
  }
}

export function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatDuration(ms) {
  if (!ms) return "—";
  const totalSecs = Math.round(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export function Loading({ label }) {
  return (
    <div className='flex items-center justify-center py-12 text-text-tertiary text-body-xs gap-3'>
      <span className='inline-block h-4 w-4 rounded-full border-2 border-accent-purple border-t-transparent animate-spin' />
      {label}
    </div>
  );
}

export function InspectModal({ event, onClose }) {
  if (!event) return null;
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs' role='dialog' aria-label='Inspect telemetry event'>
      <div className='bg-white rounded-2xl border border-border-default shadow-2xl w-full max-w-2xl overflow-hidden max-h-[85vh] flex flex-col'>
        <div className='p-4 border-b border-border-subtle flex items-center justify-between bg-surface-neutral/60'>
          <h3 className='text-body-sm font-semibold text-text-primary'>Inspect Telemetry Event</h3>
          <button type='button' onClick={onClose} aria-label='Close' className='p-1 rounded-lg hover:bg-surface-neutral transition-colors text-text-tertiary hover:text-text-secondary cursor-pointer'>
            <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>
        <div className='p-5 overflow-auto bg-gray-50 flex-1 font-mono text-[11px] leading-relaxed text-gray-800'>
          <pre className='whitespace-pre-wrap break-all select-all'>{JSON.stringify(event, null, 2)}</pre>
        </div>
        <div className='p-4 border-t border-border-subtle bg-white flex justify-end'>
          <button type='button' onClick={onClose} className='px-4 py-2 text-body-xs font-semibold bg-surface-neutral border border-border-default hover:bg-surface-neutral-subtle rounded-xl cursor-pointer'>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
